import { Typography } from '@mui/material';
import { Provider, type RestoreJob } from '@repo/api-gw-sdk';

import { QueryResultsField } from '@/app/(dashboard)/jobs/[tab]/queryResultsField';
import { PropertyType, type FilterProperty } from '@/types/advanceFilter';

import { CopyToClipboardWrapper } from '../buttons/copyToClipboardButton';

export const queryResults: FilterProperty<RestoreJob> = {
  name: 'queryResults',
  displayName: 'Query results',
  type: PropertyType.String,
  group: 'restore-to',
  field: {
    Component: ({ entity }) => {
      return (
        entity.destinationDetails.restoreResult?.recordRestore
          ?.outputLocations && <QueryResultsField entity={entity} />
      );
    },
  },
};

export const snapshotId: FilterProperty<RestoreJob> = {
  name: 'snapshotId',
  displayName: '  ',
  type: PropertyType.String,
  group: 'restore-to',
  field: {
    Component: ({ entity }) => {
      return (
        entity.destinationDetails.restoreResult?.awsEbsSnapshot?.snapshotId && (
          <Typography>
            {entity.destinationDetails.restoreResult.awsEbsSnapshot.snapshotId}
          </Typography>
        )
      );
    },
  },
};

export const instanceId: FilterProperty<RestoreJob> = {
  name: 'instanceId',
  displayName: 'Instance',
  type: PropertyType.String,
  group: 'restore-to',
  field: {
    Component: ({ entity }) => {
      return (
        entity.destinationDetails.restoreResult?.awsEc2Instance?.instanceId && (
          <Typography>
            {entity.destinationDetails.restoreResult.awsEc2Instance.instanceId}
          </Typography>
        )
      );
    },
  },
};

export const volumeId: FilterProperty<RestoreJob> = {
  name: 'volumeId',
  displayName: 'Volume',
  type: PropertyType.String,
  group: 'restore-to',
  field: {
    Component: ({ entity }) => {
      return (
        entity.destinationDetails.restoreResult?.awsEbsVolume?.volumeId && (
          <Typography>
            {entity.destinationDetails.restoreResult.awsEbsVolume.volumeId}
          </Typography>
        )
      );
    },
  },
};

export const diskId: FilterProperty<RestoreJob> = {
  name: 'diskId',
  displayName: 'Disk',
  type: PropertyType.String,
  group: 'restore-to',
  field: {
    Component: ({ entity }) => {
      return (
        entity.destinationDetails.restoreResult?.azureDisk?.diskId && (
          <Typography>
            {entity.destinationDetails.restoreResult.azureDisk.diskId}
          </Typography>
        )
      );
    },
  },
};

export const imageId: FilterProperty<RestoreJob> = {
  name: 'imageId',
  displayName: 'Image',
  type: PropertyType.String,
  group: 'restore-to',
  field: {
    Component: ({ entity }) => {
      return (
        entity.destinationDetails.restoreResult?.awsEc2Ami?.imageId && (
          <Typography>
            {entity.destinationDetails.restoreResult.awsEc2Ami.imageId}
          </Typography>
        )
      );
    },
  },
};

export const restoredResourceName: FilterProperty<RestoreJob> = {
  name: 'restoredResourceName',
  displayName: 'Restored Name',
  type: PropertyType.String,
  group: 'restore-to',
  field: {
    Component: ({ entity }) => {
      const restoredName =
        entity.destinationDetails.typeSpecificConfig?.rdsInstance?.restoredName;
      return (
        restoredName && (
          <CopyToClipboardWrapper textToCopy={restoredName}>
            <Typography>{restoredName}</Typography>
          </CopyToClipboardWrapper>
        )
      );
    },
  },
};

export const restoredPersistentVolumes: FilterProperty<RestoreJob> = {
  name: 'restoredPersistentVolumes',
  displayName: 'Restored Persistent Volumes',
  type: PropertyType.String,
  group: 'restore-to',
  field: {
    Component: ({ entity }) => {
      return (
        entity.destinationDetails.restoreResult?.eksNamespaceRestore
          ?.volumes && (
          <Typography>
            {entity.destinationDetails.restoreResult.eksNamespaceRestore.volumes
              .map((x: { restoredVolumeId: string }) => x.restoredVolumeId)
              .filter((x: string) => !!x)
              .join(', ')}
          </Typography>
        )
      );
    },
  },
};

export const destinationBucket: FilterProperty<RestoreJob> = {
  name: 'destinationBucket',
  displayName: 'Destination Bucket',
  type: PropertyType.String,
  group: 'restore-to',
  field: {
    getTitle: (entity) =>
      entity.destinationDetails.cloudProvider === Provider.Aws ||
      entity.destinationDetails.cloudProvider === Provider.Gcp
        ? 'Bucket'
        : 'Storage Account',
    Component: ({ entity }) => {
      const bucketName =
        entity.destinationDetails.typeSpecificConfig?.objectStorage?.s3Bucket
          ?.bucketName ||
        entity.destinationDetails.typeSpecificConfig?.objectStorage
          ?.storageAccount?.name ||
        entity.destinationDetails.typeSpecificConfig?.objectStorage?.gcsBucket
          ?.bucketName ||
        '';

      return bucketName && <Typography>{bucketName}</Typography>;
    },
  },
};

export const destinationContainer: FilterProperty<RestoreJob> = {
  name: 'destinationContainer',
  displayName: 'Container',
  type: PropertyType.String,
  group: 'restore-to',
  field: {
    Component: ({ entity }) => {
      const containerName =
        entity.destinationDetails.typeSpecificConfig?.objectStorage
          ?.storageAccount?.container;

      return containerName && <Typography>{containerName}</Typography>;
    },
  },
};

export const destinationPrefix: FilterProperty<RestoreJob> = {
  name: 'destinationPrefix',
  displayName: 'Prefix',
  type: PropertyType.String,
  group: 'restore-to',
  field: {
    Component: ({ entity }) => {
      const prefix =
        entity.destinationDetails.typeSpecificConfig?.objectStorage?.s3Bucket
          ?.prefix ||
        entity.destinationDetails.typeSpecificConfig?.objectStorage
          ?.storageAccount?.prefix ||
        entity.destinationDetails.typeSpecificConfig?.objectStorage?.gcsBucket
          ?.prefix ||
        '';

      return prefix && <Typography>{prefix}</Typography>;
    },
  },
};
